import {doAuthenticatedQuery} from "src/services/shared/doQuery";
import {IMcpAuthorizationRequest} from "src/types/McpAuthorization";
import {
  mcpAuthorizationDecisionMutation,
  mcpAuthorizationRequestQuery,
} from "./McpAuthorizationQueries";

// Takes the graphQL response and transforms/maps it to the portal type
export const transformMcpAuthorizationRequest = (
  request: any
): IMcpAuthorizationRequest => ({
  clientName: request.client_name,
  redirectUri: request.redirect_uri,
  resource: request.resource,
  scope: request.scope ?? undefined,
  expiresAt: request.expires_at,
});

// Fetches the details of a pending MCP authorization request.
// Resolves to null (TanStack rejects undefined data) if dxn-mcp
// doesn't know the request (unknown, expired or already decided).
export const getMcpAuthorizationRequest = async ({
  accessToken,
  parameters,
  signal,
}: {
  accessToken: string,
  parameters: {request: string},
  signal?: any,
}): Promise<IMcpAuthorizationRequest | null> => {
  const queryFunction = async (client: any) => {
    return await client.request(
      mcpAuthorizationRequestQuery,
      {request: parameters.request},
    );
  };

  try {
    // the signal goes to the GraphQL client so TanStack can abort
    const response = await doAuthenticatedQuery(
      queryFunction, accessToken, signal
    );
    const request = response?.mcp_authorization_request;
    return request ? transformMcpAuthorizationRequest(request) : null;
  }
  catch(error) {
    console.error('getMcpAuthorizationRequest throwing', (error));
    throw(error)
  }
};

// Approves or denies a pending MCP authorization request.
// Resolves to the URL the browser must be sent to - the MCP client's
// redirect_uri carrying either a code (approve) or error=access_denied (deny).
export const decideMcpAuthorization = async ({
  accessToken,
  request,
  approve,
}: {
  accessToken: string,
  request: string,
  approve: boolean,
}): Promise<string> => {
  const queryFunction = async (client: any) => {
    return await client.request(
      mcpAuthorizationDecisionMutation,
      {request, approve},
    );
  };

  try {
    const response = await doAuthenticatedQuery(queryFunction, accessToken);
    const redirectTo = response?.mcp_authorization_decision?.redirect_to;
    if (!redirectTo) {
      throw new Error('mcp_authorization_decision returned no redirect_to');
    }
    return redirectTo;
  }
  catch(error) {
    console.error('decideMcpAuthorization throwing', (error));
    throw(error)
  }
};
